import React, {useState, useContext, useEffect} from 'react';
import {useHistory} from 'react-router-dom';
import {makeStyles} from '@material-ui/core/styles';
import {
    Button,
    Grid,
    Typography,
    Paper,
    CircularProgress,
    TextField,
    FormHelperText,
} from '@material-ui/core';
import usePostForm from 'hooks/usePostForm';
import {Modal, Fade, Backdrop} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import ContactSupportIcon from '@material-ui/icons/ContactSupport';
import DirectionsBoatIcon from '@material-ui/icons/DirectionsBoat';
import CloseIcon from '@material-ui/icons/Close';
import {LifeRing} from 'components/icons/icons';
import {AppCtx} from 'context/AppContext';
import Dialog from 'components/Dialog';

const useStyles = makeStyles(theme => ({
    button: {
        borderRadius: 25,
        margin: '1rem',
    },

    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        outline: 'none',
    },
    backdrop: {
        backdropFilter: 'blur(5px)',
    },
    root: {
        width: '50%',
        margin: 'auto',
        padding: '1rem',
        maxWidth: 500,
        outline: 0,

        [theme.breakpoints.down('xs')]: {
            width: '100%',
        },
        [theme.breakpoints.only('sm')]: {
            width: '90%',
        },
    },
    form: {
        width: '100%',
        padding: '1rem',
    },
    paper: {
        padding: '1rem',
        margin: 'auto',
        maxWidth: 500,
    },
}));

const FixedSupportModal = ({modalOpen, setModalOpen}) => {
    const classes = useStyles();
    const {setSnackProps, setSnackOpen} = useContext(AppCtx);
    const [responseText, setResponseText] = useState('');
    const [showError, setShowError] = useState(null);

    //Support posting
    const {submissionInProgress, error, result, post} = usePostForm('feedback');

    const handleSubmit = () => {
        if (responseText !== '') {
            post({
                feedback_text: responseText,
                frontend_state: {
                    ...JSON.parse(localStorage.getItem('__STATE_MACHINE__')),
                    _ui_state: {
                        href: window?.location?.href,
                    },
                },
            });
        } else {
            setShowError(true);
        }
    };

    useEffect(() => {
        setShowError(false);
    }, [responseText]);

    useEffect(() => {
        if (result?.status === 'ok') {
            setSnackProps({
                severity: 'success',
                message:
                    'Thanks for contacting us. We will get back to you as soon as we can!',
            });
            setSnackOpen(true);
            setModalOpen(false);
            setResponseText('');
        } else if (result?.errors?.length || error?.errors?.length) {
            setSnackProps({
                severity: 'error',
                message: `Something went wrong. Errors: ${JSON.stringify(
                    error
                )}`,
            });
            setSnackOpen(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result, error]);

    return (
        <Modal
            className={classes.modal}
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            closeAfterTransition
            outline='none'
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 500,
                className: classes.backdrop,
            }}
            keepMounted
            disablePortal
        >
            <Fade in={modalOpen}>
                <Grid className={classes.root}>
                    <Paper className={classes.paper}>
                        <Grid
                            container
                            direction='row'
                            justifyContent='flex-end'
                            alignItems='flex-start'
                        >
                            <IconButton
                                color='primary'
                                aria-label='close'
                                edge='end'
                                onClick={() => setModalOpen(false)}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Grid>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant='h6'>
                                    I have encountered an issue and require
                                    assistance.
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    name='feedback_text'
                                    label='What went wrong? Let us know'
                                    multiline
                                    rows={8}
                                    fullWidth
                                    placeholder='Describe the issue here.'
                                    variant='outlined'
                                    value={responseText}
                                    onChange={e =>
                                        setResponseText(e.target.value)
                                    }
                                />
                            </Grid>
                            {showError ? (
                                <Grid item xs={12}>
                                    <FormHelperText error>
                                        Please describe your issue in the text
                                        box.
                                    </FormHelperText>
                                </Grid>
                            ) : null}
                            <Grid item xs={12}>
                                <Button
                                    type='submit'
                                    variant='contained'
                                    color='secondary'
                                    onClick={handleSubmit}
                                >
                                    Send
                                </Button>
                            </Grid>
                            {submissionInProgress ? (
                                <Grid item xs={12}>
                                    <CircularProgress />
                                </Grid>
                            ) : null}
                        </Grid>
                    </Paper>
                </Grid>
            </Fade>
        </Modal>
    );
};

const FixedSupport = ({size = 'small', rounded = true}) => {
    const classes = useStyles();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const history = useHistory();

    const actions = [
        {
            icon: <DirectionsBoatIcon />,
            name: 'USCG Licensing Questions',
            onClick: () => {
                history.push('/guidance/license-faq');
                setDialogOpen(false);
            }
        },
        {
            icon: <ContactSupportIcon />,
            name: 'Report a Site Issue',
            onClick: () => {
                setModalOpen(value => !value);
                setDialogOpen(false);
            }
        },
    ];

    return [
        <Button
            variant='contained'
            className={rounded ? classes.button : null}
            size={size}
            color='secondary'
            aria-label='support'
            onClick={() => setDialogOpen(true)}
            startIcon={<LifeRing />}
            key='1'
            data-analytics-id='sos-help'
        >
            Help
        </Button>,
        <FixedSupportModal
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            key='2'
        />,
        <Dialog
            onClose={() => setDialogOpen(false)}
            open={dialogOpen}
            label='How can we help?'
            options={actions}
        />
    ];
};

export default FixedSupport;
export {FixedSupportModal};
